// React
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Resources
import { createClinic, updateClinic } from "API/Resource/clinic";
import { getAllClients } from "API/Resource/client";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { selectAllClients, setAllClients } from "Store/ClientSlice";
import { selectUserEmail } from "Store/AuthSlice";
import { openSnackbar } from "Store/SnackbarSlice";

// Material UI
import { Box, Typography, Fade, CircularProgress } from "@mui/material";

// Components
import { MNStepper } from "Common/Components";

// Formik
import { Formik } from "formik";

// Validation
import { clinicValidationSchema } from "Validation";
import { parsePhoneNumberFromString } from "libphonenumber-js";

// Constants
import { schedules } from "Constants/ClinicConstants";
import { emeraldGreen } from "Constants/ColourConstants";

// Components
import {
  GeneralInfoPage,
  SchedulePage,
  AccuroPage,
  NotificationSettingsPage,
  ReviewPage,
} from "./Pages";

// Common Admin Components / Utils
import {
  NavigationButtons,
  ReviewUpdateDialog,
  SuccessDialog,
  PageTabs,
  lookupStepForError,
  handleNewStepClick,
} from "../Common";

// Styles
import { textStyles, commonStyles } from "Common/Styles";

// Libraries
import { isEqual } from "lodash";
import { DateTime } from "luxon";
import { ConfirmLeaveModal } from "Common/Modals/ConfirmLeaveModal";

// Form default values
const defaultValues = {
  name: "",
  address: "",
  city: "",
  postalCode: "",
  province: "",
  phone: "",
  altPhone: "",
  french: { name: "", address: "" },
  appointmentSchedule: schedules[0].value,
  appointmentSuggestions: [
    {
      suggestion: "",
      type: [],
      length: "",
      isVisible: false,
      overrideProviderIds: "",
    },
  ],
  accuroClientId: "",
  accuroClientSecret: "",
  accuroSubscriptionKey: "",
  accuroUUID: "",
  accuroOfficeName: "",
  appointmentTypeId: "",
  clientId: "",
  location: "",
  hoursOfOperationUrl: "",
  customMessagesEnabled: false,
  customTextMessages: {},
};

const ClinicSettings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const locationHook = useLocation();
  const allClients = useSelector(selectAllClients);

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(null);

  const [loadingClients, setLoadingClients] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [appointmentTypes, setAppointmentTypes] = useState([]);

  const [page, setPage] = useState(0);
  const [saving, setSaving] = useState(false);
  const [latestStepCompleted, setLatestStepCompleted] = useState(0);

  const [completed, setCompleted] = useState(false);

  const originalClinic = locationHook.state?.clinic;
  const clinicProp = React.useMemo(() => {
    return originalClinic ? { ...originalClinic } : undefined;
  }, [originalClinic]);
  const editClinic = Boolean(clinicProp);
  const [clinicValues, setClinicValues] = useState(defaultValues);

  const [hasTypeId, setHasTypeId] = useState(false);
  const userEmail = useSelector(selectUserEmail);
  const [isCustomMessagesChecked, setIsCustomMessagesChecked] = useState(false);

  const [stepFieldsMap, setStepFieldsMap] = useState({
    "General Information": [
      "name",
      "address",
      "city",
      "postalCode",
      "province",
      "phone",
      "altPhone",
      "french",
      "hoursOfOperationUrl",
      "clientId",
    ],
    "Appointment Schedule": [
      "location",
      "appointmentSchedule",
      "appointmentSuggestions",
    ],
    "Accuro Information": [
      "accuroOfficeName",
      "accuroClientId",
      "accuroClientSecret",
      "accuroSubscriptionKey",
      "accuroUUID",
      "appointmentTypeId",
    ],
    "Notification Settings": ["customMessagesEnabled"],
  });

  const stepComponents = useMemo(
    () => ({
      "General Information": (props) => <GeneralInfoPage {...props} />,
      "Appointment Schedule": (props) => <SchedulePage {...props} />,
      "Accuro Information": (props) => <AccuroPage {...props} />,
      "Notification Settings": (props) => (
        <NotificationSettingsPage {...props} />
      ),
      Review: (props) => <ReviewPage {...props} />,
    }),
    []
  );

  const clinicSteps = [
    "General Information",
    "Appointment Schedule",
    "Accuro Information",
    "Notification Settings",
    "Review",
  ];

  const handleOpenSnackbar = (message, severity = "error") => {
    dispatch(openSnackbar({ message, severity }));
  };

  useEffect(() => {
    const fetchClientData = async () => {
      setPage(0);
      setHasTypeId(false);

      let response = allClients;
      if (!response || response.length === 0) {
        response = await getAllClients(false);
        dispatch(setAllClients(response));
      }

      if (clinicProp) {
        clinicProp.appointmentSuggestions =
          clinicProp.appointmentSuggestions.map((suggestion) => ({
            ...suggestion,
            ...(Array.isArray(suggestion.overrideProviderIds) && {
              overrideProviderIds: suggestion.overrideProviderIds.join(", "),
            }),
          }));

        const client = response.find(
          (client) => client.id === clinicProp?.clientId
        );
        // Clear types if the client's appointment types have changed
        if (client) {
          const apptTypes = client.appointmentReasons.map(
            (reason) => reason.en
          );

          const updatedSuggestions = clinicProp.appointmentSuggestions.map(
            (suggestion) => {
              if (suggestion.type.some((type) => !apptTypes.includes(type))) {
                return { ...suggestion, type: [] };
              }
              return suggestion;
            }
          );

          const updatedClinic = {
            ...defaultValues,
            ...clinicProp,
            appointmentSuggestions: updatedSuggestions,
          };
          setClinicValues(updatedClinic);
          setHasTypeId(!!clinicProp.appointmentTypeId);
          if (!!clinicProp.customMessagesEnabled) {
            setIsCustomMessagesChecked(true);
            setStepFieldsMap((prev) => {
              const notificationFields = [...prev["Notification Settings"]];
              if (!notificationFields.includes("customMessagesEnabled")) {
                notificationFields.push("customMessagesEnabled");
              }
              return {
                ...prev,
                "Notification Settings": notificationFields,
              };
            });
          }
          setSelectedClient(client);
          setAppointmentTypes(
            client.appointmentReasons.map((reason) => reason.en)
          );
        } else {
          setClinicValues({ ...defaultValues, ...clinicProp });
          setSelectedClient(null);
          setAppointmentTypes([]);
        }
      } else {
        setClinicValues(defaultValues);
        setSelectedClient(null);
        setAppointmentTypes([]);
      }
      setAllClients(response);
      setLoadingClients(false);
    };
    fetchClientData();
  }, [clinicProp, dispatch, allClients]);

  const handleClinicSave = async (values) => {
    setSaving(true);
    values.appointmentSuggestions = values.appointmentSuggestions.map(
      (suggestion) => ({
        ...suggestion,
        ...(suggestion.overrideProviderIds !== null &&
          typeof suggestion.overrideProviderIds === "string" && {
            overrideProviderIds:
              suggestion.overrideProviderIds.trim() === ""
                ? []
                : suggestion.overrideProviderIds
                    .split(",")
                    .map((val) => Number(val.trim()))
                    .filter((num) => !isNaN(num)),
          }),
      })
    );
    clinicProp
      ? await handleUpdateClinic(values)
      : await handleCreateClinic(values);
    setSaving(false);
  };

  const handleCreateClinic = async (values) => {
    try {
      await createClinic(values);
      setConfirmationOpen(true);
    } catch (error) {
      dispatch(openSnackbar({ message: error.message, severity: "error" }));
    }
  };

  const handleUpdateClinic = async (values) => {
    const changes = Object.keys(values).reduce((acc, key) => {
      if (!isEqual(values[key], originalClinic[key])) {
        acc[key] = values[key];
      }
      return acc;
    }, {});

    if (Object.keys(changes).length === 0) {
      handleOpenSnackbar(
        "Please make sure to change at least one field to update the clinic.",
        "info"
      );
    } else {
      setPendingChanges(changes);
      setUpdateDialogOpen(true);
    }
  };

  const processClinicChanges = async () => {
    try {
      if (Object.keys(pendingChanges).length > 0) {
        await updateClinic({
          id: clinicProp.id,
          adminUpdate: {
            updatedBy: userEmail,
            updatedAt: DateTime.now().toISO(),
          },
          ...pendingChanges,
          attentionRequired: false,
        });
        setConfirmationOpen(true);
      } else {
        navigateToClientManagement();
      }
    } catch (error) {
      dispatch(openSnackbar({ message: error.message, severity: "error" }));
    }
  };

  const navigateToClientManagement = () => {
    navigate("/ClientManagement");
  };

  const handleSaveClick = async (formik) => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      const stepName = lookupStepForError(
        Object.keys(errors)[0],
        stepFieldsMap
      );
      handleOpenSnackbar(`Please fix all errors at the ${stepName} step`);
      return;
    }
    formik.handleSubmit();
    setCompleted(true);
  };

  return (
    <Formik
      initialValues={clinicValues ?? defaultValues}
      enableReinitialize={true}
      validationSchema={clinicValidationSchema(selectedClient)}
      onSubmit={(values) => {
        const formattedValues = {
          ...values,
          phone: parsePhoneNumberFromString(
            values.phone,
            "CA"
          )?.formatNational(),
          altPhone: parsePhoneNumberFromString(
            values.altPhone,
            "CA"
          )?.formatNational(),
        };

        if (!hasTypeId) {
          delete formattedValues.appointmentTypeId;
        }
        handleClinicSave(formattedValues);
      }}
    >
      {(formik) => {
        const StepComponent = stepComponents[clinicSteps[page]];
        const currentStepFields = stepFieldsMap[clinicSteps[page]] || [];

        return (
          <form onSubmit={formik.handleSubmit}>
            <ReviewUpdateDialog
              open={updateDialogOpen}
              onClose={() => setUpdateDialogOpen(false)}
              onConfirm={() => processClinicChanges()}
              account={"Clinic"}
              reviewPage={
                <ReviewPage
                  formik={formik}
                  hasTypeId={hasTypeId}
                  setHasTypeId={setHasTypeId}
                  clinicProp={clinicProp}
                  allClients={allClients}
                  selectedClient={selectedClient}
                />
              }
              finishLoading={confirmationOpen}
            />
            <SuccessDialog
              onOpen={confirmationOpen}
              onClose={() => {
                setConfirmationOpen(false);
                navigateToClientManagement();
              }}
              accountName={formik.values.name}
              isUpdating={editClinic}
            />

            <Box sx={commonStyles.pageBox}>
              <Typography sx={textStyles.title}>
                {editClinic
                  ? `Edit Clinic: ${clinicProp.name}`
                  : "Create Clinic"}
              </Typography>

              {editClinic ? (
                <PageTabs
                  formik={formik}
                  clientSteps={clinicSteps}
                  stepFieldsMap={stepFieldsMap}
                  currentStepFields={currentStepFields}
                  page={page}
                  setPage={setPage}
                  handleOpenSnackbar={handleOpenSnackbar}
                />
              ) : (
                <MNStepper
                  steps={clinicSteps}
                  activeStep={page}
                  latestStepCompleted={latestStepCompleted}
                  onStepClick={(stepIndex) =>
                    handleNewStepClick(
                      formik,
                      currentStepFields,
                      stepIndex,
                      setPage,
                      setLatestStepCompleted,
                      handleOpenSnackbar
                    )
                  }
                />
              )}

              {/* Step Content */}
              {!originalClinic || !loadingClients ? (
                <Fade in={true} key={page} timeout={300}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {StepComponent &&
                      StepComponent({
                        formik,
                        hasTypeId,
                        setHasTypeId,
                        clinicProp,
                        allClients,
                        selectedClient,
                        setSelectedClient,
                        appointmentTypes,
                        setAppointmentTypes,
                        loadingClients,
                        isCustomMessagesChecked,
                        setIsCustomMessagesChecked,
                        setStepFieldsMap,
                      })}
                  </Box>
                </Fade>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress
                    sx={{ marginTop: "5%", color: emeraldGreen }}
                  />
                </Box>
              )}
              {/* Form Buttons */}
              <NavigationButtons
                page={page}
                editAccount={originalClinic}
                lastPageNumber={clinicSteps.length - 1}
                cancelClick={navigateToClientManagement}
                nextClick={() =>
                  handleNewStepClick(
                    formik,
                    currentStepFields,
                    page + 1,
                    setPage,
                    setLatestStepCompleted,
                    handleOpenSnackbar
                  )
                }
                backClick={() => setPage(page - 1)}
                saveClick={() => handleSaveClick(formik)}
                loading={saving}
              />
            </Box>
            <ConfirmLeaveModal
              onlyBlockWhen={
                !isEqual(formik.values, formik.initialValues) && !completed
              }
            />
          </form>
        );
      }}
    </Formik>
  );
};

export default ClinicSettings;
